import { createContext, useState, useContext } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './AuthProvider'
import { useConfig } from './ConfigProvider';
import {
    AddUser, EditUser, SendResetPasswordToUser, SetUserIsActiveValue, GetCustomerUsersWithoutEHandelAcces,
    DeleteUserWithoutEHandelAcces, AddCustomerUserWithoutEHandelAcces, EditCustomerUserWithoutEHandelAcces
} from '../Services/UserService';
import { USERS_QUERY_KEY } from '../Hooks/Queries/useUsersQueries';
import { toast } from 'react-toastify';

export const UserContext = createContext()

export const useUser = () => {
    return useContext(UserContext);
};

export const UserProvider = ({ children }) => {
    const { authState } = useAuth()
    const { api } = useConfig()
    const queryClient = useQueryClient()
    const [isAddingEditingCustomerUser, setIsAddingEditingCustomerUser] = useState(false);

    // Listę użytkowników i słownik ról pobierają teraz hooki useCustomerUsers/useUserRoles
    // bezpośrednio w komponentach panelu. Tutaj zostaje tylko unieważnianie cache po zapisie.
    const handleGetCustomerUsers = () => {
        queryClient.invalidateQueries({ queryKey: [...USERS_QUERY_KEY, 'customerUsers'] })
    }

    const handleGetCustomerUsersWithoutEHandelAccess = async () => {
        try {
            const res = await GetCustomerUsersWithoutEHandelAcces(api, authState.custId)
            return res.data
        } catch (e) {
            console.log(e)
            return null
        }
    }

    const handleDeleteUserWithoutEHandelAcces = async (recNo, custId, contactNumber, userCode, userLogin) => {
        try {
            let res = await DeleteUserWithoutEHandelAcces(api, recNo, custId, contactNumber, userCode, userLogin)
            return res.data
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const handleAddCustomerUserWithoutEHandelAcces = async (customerUser) => {
        try {
            let res = await AddCustomerUserWithoutEHandelAcces(api, customerUser)
            return res.data
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const handleEditCustomerUserWithoutEHandelAcces = async (customerUser) => {
        try {
            let res = await EditCustomerUserWithoutEHandelAcces(api, customerUser)
            return res.data
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const addCustomerUser = async (user) => {
        setIsAddingEditingCustomerUser(true)
        try {
            const res = await AddUser(api, user)
            handleGetCustomerUsers()
            return res.status
        } catch (error) {
            console.log(error)
            return error.response.data
        }
        finally {
            setIsAddingEditingCustomerUser(false)
        }
    }

    const updateCustomerUser = async (user) => {
        setIsAddingEditingCustomerUser(true)
        try {
            const res = await EditUser(api, user)
            setIsAddingEditingCustomerUser(false)
            handleGetCustomerUsers()
            return res.status
        } catch (error) {
            setIsAddingEditingCustomerUser(false)
            console.log(error)
            return error.response.data
        }
    }

    const handleUserLockUnlock = (value, userId) => {
        SetUserIsActiveValue(api, value, userId).then(() => {
            handleGetCustomerUsers()
        }).catch(e => {
            console.log(e)
        })
    }

    const handleSendResetPasswordToUser = async (userId) => {
        try {
            await SendResetPasswordToUser(api, userId)
            toast.success("Link do resetu hasła został wysłany.")
        } catch (error) {
            console.log(error)
            toast.error("Nie udało się wysłać linku do resetu hasła.")
        }
    }

    return (
        <UserContext.Provider value={{
            isAddingEditingCustomerUser,
            addCustomerUser,
            handleGetCustomerUsers,
            updateCustomerUser,
            handleUserLockUnlock,
            handleSendResetPasswordToUser,
            handleGetCustomerUsersWithoutEHandelAccess,
            handleDeleteUserWithoutEHandelAcces,
            handleAddCustomerUserWithoutEHandelAcces,
            handleEditCustomerUserWithoutEHandelAcces,
        }}>
            {children}
        </UserContext.Provider>
    )
}